import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Handshake, ExternalLink, ArrowRight } from "lucide-react";

const TIERS = [
  { key: "principal", label: "Partenaire principal" },
  { key: "founder", label: "Partenaires fondateurs" },
  { key: "partner", label: "Partenaires" },
];

const PartnerLogo = ({ p }) => {
  const inner = (
    <div className="group flex flex-col items-center text-center gap-3 rounded-xl border border-[#272A34] hover:border-amber-500/50 bg-[#121418] p-6 transition-all hover:-translate-y-1 h-full">
      {p.logo ? <img src={p.logo} alt={p.name} className="h-16 object-contain" /> : <span className="h-16 w-16 rounded-lg bg-amber-900/40 flex items-center justify-center"><Handshake className="w-7 h-7 text-amber-400" /></span>}
      <div className="font-serif text-stone-100 group-hover:text-amber-100">{p.name}</div>
      {p.description && <p className="text-xs text-stone-500 line-clamp-2">{p.description}</p>}
      {p.website && <span className="text-xs font-mono text-amber-500 inline-flex items-center gap-1">Site <ExternalLink className="w-3 h-3" /></span>}
    </div>
  );
  return p.website ? <a href={p.website} target="_blank" rel="noopener noreferrer" data-testid={`partner-logo-${p.id}`}>{inner}</a> : <div data-testid={`partner-logo-${p.id}`}>{inner}</div>;
};

export default function Partners() {
  const { data: partners = [] } = useQuery({ queryKey: ["partnersPublic"], queryFn: async () => (await api.get("/partners")).data });
  const byTier = (k) => partners.filter((p) => p.tier === k);

  return (
    <>
      <Seo title="Nos partenaires" description="Les partenaires officiels de Swiss Pagan." canonicalPath="/partenaires" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-3"><Handshake className="w-7 h-7 text-amber-500" /><h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">Nos partenaires</h1></div>
        <p className="text-stone-400 mb-12 max-w-2xl">Les marques et organisations qui soutiennent Swiss Pagan et l'univers Pagan, Viking et médiéval en Suisse.</p>

        {partners.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#272A34] bg-[#121418] p-12 text-center" data-testid="partners-empty">
            <Handshake className="w-10 h-10 text-amber-600/70 mx-auto mb-4" />
            <p className="text-stone-400 max-w-md mx-auto mb-6">Nous construisons notre cercle de partenaires. Vous souhaitez en faire partie dès le début ?</p>
            <Link to="/devenir-partenaire" data-testid="partners-become-cta" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md border border-amber-400/30 transition-all">Devenir partenaire <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          TIERS.map(({ key, label }) => {
            const list = byTier(key);
            if (list.length === 0) return null;
            return (
              <section key={key} className="mb-14" data-testid={`partners-tier-${key}`}>
                <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-6 border-b border-amber-900/30 pb-3">{label}</h2>
                <div className={`grid gap-5 ${key === "principal" ? "sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`}>
                  {list.map((p) => <PartnerLogo key={p.id} p={p} />)}
                </div>
              </section>
            );
          })
        )}

        {partners.length > 0 && (
          <div className="mt-8 text-center">
            <Link to="/devenir-partenaire" data-testid="partners-become-cta" className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-sm uppercase tracking-wider">Devenir partenaire <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
      </div>
    </>
  );
}
